import React, { useState, useEffect, useCallback } from 'react';
import './PinnedMessages.css';

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');

const PinnedMessages = ({ 
  roomId, 
  roomType,
  token, 
  user, 
  onClose, 
  socket,
  onUnpinMessage,
  isAdmin = false 
}) => {
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch pinned messages
  const fetchPinnedMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${API_BASE}/api/chat/rooms/${roomId}/pinned`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPinnedMessages(data.pinnedMessages || []);
      } else {
        throw new Error('Failed to fetch pinned messages');
      }
    } catch (err) {
      console.error('Error fetching pinned messages:', err);
      setError('Failed to load pinned messages');
    } finally {
      setLoading(false);
    }
  }, [roomId, token]);

  // Initial fetch
  useEffect(() => {
    fetchPinnedMessages();
  }, [fetchPinnedMessages]);

  // Listen for pin/unpin events
  useEffect(() => {
    if (!socket) return;

    const handleMessagePinned = (data) => {
      if (data.roomId === roomId) {
        fetchPinnedMessages(); // Refresh the list
      }
    };

    const handleMessageUnpinned = (data) => {
      if (data.roomId === roomId) {
        setPinnedMessages(prev => 
          prev.filter(pin => pin.message._id !== data.messageId)
        );
      }
    };

    socket.on('message:pinned', handleMessagePinned);
    socket.on('message:unpinned', handleMessageUnpinned);

    return () => {
      socket.off('message:pinned', handleMessagePinned);
      socket.off('message:unpinned', handleMessageUnpinned);
    };
  }, [socket, roomId, fetchPinnedMessages]);

  // Unpin message
  const handleUnpin = async (messageId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/chat/rooms/${roomId}/messages/${messageId}/pin`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to unpin message');
      }

      // Remove from local state immediately for better UX
      setPinnedMessages(prev => 
        prev.filter(pin => pin.message._id !== messageId)
      );
      
      // Notify parent component to update its state
      if (onUnpinMessage) {
        onUnpinMessage(messageId);
      }
    } catch (err) {
      console.error('Error unpinning message:', err);
      // Optionally show error notification
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  };

  return (
    <div className="pinned-messages">
      <div className="pinned-header">
        <div className="pinned-title">
          <span className="pin-icon">📌</span>
          <h3>Pinned Messages</h3>
          <span className="pin-count">({pinnedMessages.length})</span>
        </div>
        <button className="pinned-close-btn" onClick={onClose} aria-label="Close pinned messages">
          ×
        </button>
      </div>

      <div className="pinned-content">
        {loading ? (
          <div className="pinned-loading">Loading pinned messages...</div>
        ) : error ? (
          <div className="pinned-error">
            {error}
            <button onClick={fetchPinnedMessages} className="retry-btn">Retry</button>
          </div>
        ) : pinnedMessages.length === 0 ? (
          <div className="no-pinned-messages">
            <div className="empty-state">
              <span className="empty-icon">📌</span>
              <h4>No pinned messages</h4>
              <p>Important messages that are pinned by admins will appear here.</p>
            </div>
          </div>
        ) : (
          <div className="pinned-list">
            {pinnedMessages.map((pin) => (
              <div key={pin.message._id} className="pinned-message">
                <div className="pinned-message-header">
                  <div className="message-avatar">
                    {getUserInitials(pin.message.sender?.name || 'Unknown')}
                  </div>
                  <div className="message-info">
                    <div className="message-sender">
                      {pin.message.sender?.name || 'Unknown'}
                    </div>
                    <div className="message-time">
                      {formatTime(pin.message.createdAt)}
                    </div>
                  </div>
                  {/* Show unpin button for admins in regular rooms, or anyone in DMs */}
                  {(isAdmin || roomType === 'direct') && (
                    <button 
                      className="unpin-btn"
                      onClick={() => handleUnpin(pin.message._id)}
                      title="Unpin message"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                <div className="pinned-message-content">
                  <div className="message-text">{pin.message.content}</div>
                  
                  {pin.message.attachments && pin.message.attachments.length > 0 && (
                    <div className="message-attachments">
                      {pin.message.attachments.map((attachment, index) => (
                        <div key={index} className="attachment">
                          📎 {attachment.originalName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="pin-info">
                  <span className="pin-icon-small">📌</span>
                  Pinned by {pin.pinnedBy?.name || 'Unknown'} on {formatTime(pin.pinnedAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PinnedMessages;
